import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { FormField } from '../../components/ui/form-field';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { useToast } from '../../components/ui/toast';
import { FlexFieldsV2 } from '../../components/deal-form/FlexFieldsV2';
import { useDealForm } from '../../hooks/use-deal-form';
import {
  selectDealTypeId,
  selectDealTypesConfigMap,
} from '../../../application/store/reducers/deals';

// ── Component ───────────────────────────────────────────────

export function DealFormPage() {
  const navigate = useNavigate();
  const { dealId } = useParams<{ dealId?: string }>();
  const toast = useToast();

  // Redux state
  const reduxDealTypeId = useSelector(selectDealTypeId);
  const reduxDealTypesConfigMap = useSelector(selectDealTypesConfigMap);

  const categoryId = reduxDealTypeId || '';
  const config = categoryId
    ? reduxDealTypesConfigMap[categoryId] || null
    : null;

  // Deal form hook
  const { initialValues, isFormLoading, isSaving, flexFieldsConfig, saveDeal } =
    useDealForm({
      dealId,
      categoryId,
      config,
    });

  // Local state
  const [flexFieldData, setFlexFieldData] = useState<Record<string, any>>({});
  const [notes, setNotes] = useState('');
  const [canSave, setCanSave] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Sync notes from initial values
  useEffect(() => {
    if (initialValues.notes) {
      setNotes(initialValues.notes);
    }
  }, [initialValues.notes]);

  // Handle flex data changes
  const handleFlexDataChange = useCallback((flexJSON: Record<string, any>) => {
    setFlexFieldData(flexJSON);
    setIsDirty(true);
  }, []);

  // Handle can save changes
  const handleCanSaveChange = useCallback((value: boolean) => {
    setCanSave(value);
  }, []);

  // Handle notes change
  const handleNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNotes(e.target.value);
      setIsDirty(true);
    },
    []
  );

  // Handle cancel/back
  const handleBack = useCallback(() => {
    if (isDirty) {
      setShowCancelModal(true);
    } else {
      navigate('/v2/pipeline');
    }
  }, [isDirty, navigate]);

  // Handle save
  const handleSave = useCallback(async () => {
    setSubmitted(true);

    if (!canSave) {
      toast.error('Please fix all errors before saving');
      return;
    }

    const values = {
      ...initialValues,
      notes,
      flexFieldData,
    };

    const result = await saveDeal(values);

    if (result.success) {
      toast.success(
        dealId ? 'Deal updated successfully' : 'Deal created successfully'
      );
      // Navigate back to pipeline after a short delay
      setTimeout(() => {
        navigate('/v2/pipeline');
      }, 500);
    } else {
      toast.error(result.error || 'There was an error saving your deal');
    }
  }, [
    canSave,
    initialValues,
    notes,
    flexFieldData,
    saveDeal,
    dealId,
    navigate,
    toast,
  ]);

  // Loading state
  if (isFormLoading || !config) {
    return (
      <div className="flex flex-col h-full">
        <DealFormHeader
          isEditing={!!dealId}
          onBack={() => navigate('/v2/pipeline')}
        />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // No deal type selected
  if (!categoryId) {
    return (
      <div className="flex flex-col h-full">
        <DealFormHeader
          isEditing={!!dealId}
          onBack={() => navigate('/v2/pipeline')}
        />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">
            No deal type selected. Please select a deal type from the pipeline.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DealFormHeader isEditing={!!dealId} onBack={handleBack} />

      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Flex Fields */}
          <FlexFieldsV2
            flexFieldsConfig={flexFieldsConfig}
            initialValues={initialValues.flexFieldData || {}}
            submitted={submitted}
            currentDispositionID={initialValues.currentDispositionID}
            onFlexDataChange={handleFlexDataChange}
            onCanSaveChange={handleCanSaveChange}
          />

          {/* Notes Section */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-4 border-b border-border bg-muted/50">
              <h3 className="text-base font-semibold text-foreground">Notes</h3>
            </div>
            <div className="p-5">
              <FormField>
                <Textarea
                  placeholder="Add notes here"
                  value={notes}
                  onChange={handleNotesChange}
                  rows={4}
                  className="resize-none"
                />
              </FormField>
            </div>
          </div>

          {/* Save Button */}
          <div className="pb-8">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full h-12 rounded-full text-base font-bold"
            >
              {isSaving ? (
                <Loader2 className="size-5 animate-spin" />
              ) : dealId ? (
                'Update deal'
              ) : (
                'Save changes'
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <ConfirmModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={() => {
          setShowCancelModal(false);
          navigate('/v2/pipeline');
        }}
        title="Leave without saving changes?"
        description="If you leave without saving, your progress will be lost."
        confirmLabel="Leave"
        cancelLabel="Keep editing"
        variant="warning"
      />
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────

function DealFormHeader({
  isEditing,
  onBack,
}: {
  isEditing: boolean;
  onBack: () => void;
}) {
  return (
    <header className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border bg-background shrink-0">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="gap-2 text-foreground"
      >
        <ArrowLeft className="size-4" />
        Cancel
      </Button>
      <h1 className="text-lg font-bold text-foreground">
        {isEditing ? 'Edit deal' : 'New deal'}
      </h1>
      <div className="w-[88px]" /> {/* Spacer for centering */}
    </header>
  );
}
